import React from "react";
import { Button, Card, Container, Image } from "semantic-ui-react";
import styled from "styled-components";
import { elevation, palette } from "@conserveit/blocks";
import { Trans } from "@lingui/macro";

const FadeoutEffect = styled.div`
  position: fixed;
  padding: 0;
  margin: 0;

  top: 0;
  left: 0;

  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1;
`;

const ModalContainer = styled(Container)`
  position: fixed;
  top: 40% !important;
`;

const ModalShadow = styled.div`
  border-radius: 5px;
  box-shadow: ${elevation.content};
`;

const ModalGroup = styled(Card.Group)`
  display: flex;
  justify-content: center;
  width: 100vw;
`;

const StyledModal = styled(Card)`
  width: 450px !important;
  background-color: ${palette.grey1} !important;
`;

const HeaderModalContainer = styled.div`
  display: flex;
  height: 150px;
  vertical-align: middle !important;
  padding-right: 1px;
  width: 100%;
  justify-content: center;
`;

const StyledHeaderContent = styled(Card.Content)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 150px;
`;

const StyledModalHeader = styled(Card.Header)`
  vertical-align: middle;
  display: flex;
  flex-direction: column;
`;

const StyledModalImage = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-top: 10px;
`;

const StyledModalRow = styled.div`
  font-weight: 700;
  font-size: 16px;
  display: flex;
  width: 100%;
  margin: 30px 0 20px 0;
`;

const ModalCardContent = styled(Card.Content)`
  display: flex;
  justify-content: center;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 300px;
`;

const ButtonWrapper = styled.div`
  margin: 0 25px;
  display: flex;
  justify-content: center;
`;

const CancelButton = styled(Button)`
  width: 100px;
  padding-left: 0 !important;
  padding-right: 0 !important;
  color: ${palette.pureWhite} !important;
  background-color: ${palette.edit} !important;
`;

interface Props {
  iconSrc: string;
  iconAlt: string;
  actionDescription: React.ReactElement;
  confirmButton: React.ReactElement;
  onModalCancel: () => void;
  message?: string;
}

const SiteDetailsModal: React.FC<Props> = ({
  iconSrc,
  iconAlt,
  actionDescription,
  confirmButton,
  onModalCancel,
  message
}) => {
  return (
    <FadeoutEffect>
      <ModalContainer>
        <ModalGroup>
          <ModalShadow>
            <StyledModal>
              <HeaderModalContainer>
                <StyledHeaderContent>
                  <StyledModalHeader>
                    <Card.Content>
                      <StyledModalImage>
                        <Image src={iconSrc} alt={iconAlt} width="50px" />
                      </StyledModalImage>
                      <StyledModalRow>
                        {message ?
                          <Trans id={message}>
                            Changes will override default values. Do you want to continue?
                          </Trans>
                          :
                          <>
                            <Trans id="site_details.modal.action_confirmation_certain">
                              Are you sure you want to
                            </Trans>
                            &nbsp;
                            {actionDescription}
                            &nbsp;
                            <Trans id="site_details.modal.action_confirmation_change">
                            these changes?
                            </Trans>
                          </>
                        }
                      </StyledModalRow>
                    </Card.Content>
                  </StyledModalHeader>
                </StyledHeaderContent>
                <StyledHeaderContent></StyledHeaderContent>
              </HeaderModalContainer>

              <ModalCardContent>
                <ModalButtonContainer>
                  <ButtonWrapper>{confirmButton}</ButtonWrapper>
                  <ButtonWrapper className="discard-modal__button">
                    <CancelButton
                      data-testid="cancel-button"
                      onClick={onModalCancel}
                    >
                      <Trans id="site_details.cancel">Cancel</Trans>
                    </CancelButton>
                  </ButtonWrapper>
                </ModalButtonContainer>
              </ModalCardContent>
            </StyledModal>
          </ModalShadow>
        </ModalGroup>
      </ModalContainer>
    </FadeoutEffect>
  );
};

export default SiteDetailsModal;
